import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import {
  Avatar, Button, Card, ErrorBoundary,
  RatingScore,
  TailwindSpinner,
} from '@components/common';
import { CardAnimations } from '@type/card';
import { getI18nLinkHref } from '@services/i18nPaths';
import { useRouter } from 'next/router';
import { useSwrJobItem } from '@hooks/useSwrApi/endpoints/job-operations';
import { useJobTalentActions } from '@hooks/features/useJobTalentActions';
import { JobPlacement } from '@type/job-params';
import { BookmarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

// Interface
// ******************************************
export interface CardJobInterviewProps {
  jobInterview: ApiSchema<'InterviewJobWithQuestion'>,
  locale: Locale,
  animation?: CardAnimations,
  className?: string,
}

/**
 * @description CardJobInterview component for jobs with pending interviews.
 *
 * - jobInterview: *ApiSchema<'InterviewJobWithQuestion'>* - The job interview item. Required.
 * - locale - *Locale* - The current app locale.
 * - animation: *CardAnimations* - The animation for the card. Optional. Default is 'riseUp'.
 * - className: *string* - Additional classes for the card. Optional.
 */
export const CardJobInterview = (props: CardJobInterviewProps) => {
  // Props
  const {
    jobInterview,
    locale,
    animation = 'riseUp',
    className = '',
  } = props;

  // Translation
  const { t } = useTranslation('common');

  const router = useRouter();
  const jobSlug = jobInterview.jobSlug || '';
  const jobTitle = jobInterview.jobTitle || '';

  // Fetch job data for company info, logo, rating
  const { data: jobData } = useSwrJobItem({
    locale,
    jobSlug,
    shouldFetch: !!jobSlug,
  });

  const {
    isFavorite,
    isLoadingFavoritesAction,
    removeFromFavorites,
    addToFavorites,
  } = useJobTalentActions({
    jobSlug: jobSlug ?? '',
    appliedProp: true,
    placement: JobPlacement.CARD,
  });


  // Common classes
  // *****************************************
  const cardClassesBase = {
    common: 'relative rounded-md bg-surface border border-y-3 overflow-hidden group',
    highlighted: false,
    custom: className || '',
  };
  const cardClasses = Object.values(cardClassesBase).join(' ');

  // Helpers for job url.
  // ************************************
  const defaultJobUrl = getI18nLinkHref(`/job/${jobSlug}`, locale);

  // Handle interview button click - navigate directly to text interview
  const handleInterviewClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    void router.push(`/job/${jobSlug}/ai-interview?type=text`);
  };

  // Render component
  // *****************************************
  return (
    <>
      <Card
        animation={animation}
        className={cardClasses}
      >
        <ErrorBoundary fallback={<div className="flex items-center justify-center p-3 text-error">Error loading job data.</div>}>

          {/* Full-card link to the job details page */}
          <Link
            href={defaultJobUrl}
            className="absolute inset-0 z-1"
            aria-label={jobTitle}
          >
            <span className="hidden">{jobTitle}</span>
          </Link>

          {/* CARD BODY */}
          <Card.Body className="relative grid grid-cols-10 !pb-0 !pl-3 !pr-5 !pt-5">
            <div className="col-span-7 flex flex-col gap-2">
              {/* Job title - max 2 lines */}
              <h2 className="line-clamp-2 text-base font-bold leading-6">{jobTitle}</h2>
              {/* Employer name */}
              <div className="mt-2 line-clamp-1 w-full text-sm text-ink-medium">
                {jobData?.companyName}
              </div>
              <div className="mb-3 rounded-md bg-warning/10 px-3 py-2 text-sm text-ink">
                {t('my-interviews.card.notification')}
              </div>
            </div>
            <div className="col-span-3">
              <Button
                type="button"
                rounding="full"
                styling="none"
                size="sm"
                className="group/bookmarkButton absolute right-0 top-0 z-2 mr-1 mt-1"
                onClick={isFavorite ? removeFromFavorites : addToFavorites}
              >
                {isLoadingFavoritesAction ? (
                  <TailwindSpinner className="size-5" />
                ) : (
                  <BookmarkIcon className={clsx(isFavorite && 'fill-current', 'size-5 text-warning group-hover/bookmarkButton:fill-current')} />
                )}
              </Button>

              {/* Employer logo */}
              <div className="relative flex h-15 items-center justify-center">
                <Avatar
                  imgPath={jobData?.companyLogo}
                  imgFolder="employer_logo"
                  extractImageName
                  name={jobData?.companyName || jobTitle}
                  size="md"
                  aspectRatio="square-fit"
                  rounding="none"
                  id={(jobData?.companyName || jobTitle).charCodeAt(0)}
                />
              </div>
              {/* Employer rating */}
              { (jobData?.rating !== undefined && jobData?.rating > 0) && (
              <div className="mt-2 flex justify-center sm:h-5">
                <RatingScore score={jobData?.rating} showScore={false} className="size-4 text-warning" />
              </div>
              )}
            </div>
          </Card.Body>

          {/* CARD FOOTER - Notification banner instead of salary/location */}
          <Card.Footer className="relative z-2 flex bg-surface p-3">
            {/* Interview CTA button - always visible */}
            <Button
              styling="solid"
              color="secondary"
              rounding="full"
              size="xs"
              className="flex flex-1 items-center justify-center overflow-hidden !text-xs"
              onClick={handleInterviewClick}
            >
              {t('ai.interview.start')}
            </Button>
          </Card.Footer>

        </ErrorBoundary>
      </Card>

    </>
  );
};

